import { Container } from '@mui/material';
import { GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import { useDispatch } from 'react-redux';
import BlackAccountHeader from '../../../../components/account/BlackAccountHeader';
import DrawerEvent from '../../../../components/account/DrawerMenu/DrawerEvent';
import VyButton from '../../../../components/common/VyButton';
import { VyLoader } from '../../../../components/common/VyLoader';
import VySEO from '../../../../components/common/VySEO';
import MobilePreviewWrapper from '../../../../components/event/pageBuilder/MobilePreviewWrapper';
import PageBgOverlay from '../../../../components/event/pageBuilder/PageBgOverlay';
import PageBuilderContent from '../../../../components/event/pageBuilder/PageBuilderContent';
import PageSettingsSidebar from '../../../../components/event/pageBuilder/PageSettingsSidebar';
import PBUpdateButtons from '../../../../components/event/pageBuilder/PBUpdateButtons';
import SignupForm from '../../../../components/event/signup/SignupForm';
import SignupHeader from '../../../../components/event/signup/SignupHeader/index';
import WithoutPageBuilder from '../../../../components/event/templates/VystemLight/WithoutPageBuilder';
import usePagebuilder from '../../../../hooks/pagebuilder/usePagebuilder';
import { useDataLoader } from '../../../../hooks/useDataLoader';
import { usePermission } from '../../../../hooks/usePermission';
import { EventInterface } from '../../../../interfaces/interfaces';
import EventWrapper from '../../../../layouts/EventWrapper';
import { setPageBuilder } from '../../../../reduxToolkit/slices/commonSlice';
import { useSelector } from '../../../../reduxToolkit/typedRedux';
import { usePostHog } from 'posthog-js/react';
import Image from 'next/image';
import logo from '../../../../public/vystemlogo.png';

import { getEventStaticPaths, getEventStaticProps } from '../../../../utils/eventStaticHelper';
import Link from 'next/link';

function SingUpForEvent({ initEvent }: { initEvent?: EventInterface }) {
  const { t } = useTranslation();
  const posthog = usePostHog();

  const { isPagebuilderActive } = usePagebuilder();
  const { loadData, isLoadingData } = useDataLoader();

  const user = useSelector((state) => state.common.user);
  const pagedata = useSelector((state) => state.common.pagedata);
  const pagebuilder = useSelector((state) => state.common.pagebuilder);
  const event = useSelector((state) => state.common.event);
  const templateEditMode = useSelector((state) => state.common.templateEditMode);
  const isMobilePreview = useSelector((state) => state.common.isMobilePreview);
  const { isUpdating, updatePageBuilder, cancelChanges } = usePagebuilder();
  const dispatch = useDispatch();
  const { isEventHost } = usePermission();

  const router = useRouter();

  useEffect(() => {
    if (initEvent) {
      loadData(initEvent, undefined, false, false, false, true);
    }
  }, [initEvent]);

  useEffect(() => {
    if (user && user.role !== 'PARTICIPANT_ANONYM' && pagedata) {
      dispatch(
        setPageBuilder({
          ...pagebuilder,
          originalpagedata: pagedata,
          currentpagedata: pagedata,
          originalcomponents: pagedata?.uicomponents || [],
          currentcomponents: pagedata?.uicomponents || [],
          originalcolumncomponents: pagedata?.uicolumncomponents || [],
          currentcolumncomponents: pagedata?.uicolumncomponents || [],
        })
      );
    }
  }, [user, pagedata]);

  useEffect(() => {
    if (event && event.organization?._id === '63b553ab5cc7d5fc56459b26') posthog.startSessionRecording();
  }, [event]);

  if (isLoadingData || !event || router.isFallback)
    return <VyLoader backgroundColor={event?.template?.mainColor} loaderColor={event?.template?.mainButtonTextColor} solidBackground={true} />;

  return (
    <>
      <VySEO title={t('event:event-signup')} vystemEvent={event} />
      <EventWrapper>
        {!pagedata ? ( // if no pagebuilder is active
          <MobilePreviewWrapper>
            <Container component="main" className={`h-full max-h-full flex flex-col items-center justify-center`}>
              <div className={`mt-2 flex flex-col items-center mb-[20%] w-full`}>
                <Image src={logo} className={`m-1 mb-5 max-w-[20%]`} data-cy="vystemlogo" alt="vystem logo" />

                {event ? (
                  <div style={{ marginTop: '20px', width: '100%', maxWidth: '1333px' }}>
                    {event.eventauth.provider === 'vystem' ? (
                      <div>
                        <SignupHeader event={event} />
                        <SignupForm event={event} eventslug={event.slug} query={router.query} />
                      </div>
                    ) : (
                      <>
                        <div
                          style={{
                            fontSize: '20px',
                            textAlign: 'center',
                            width: '100%',
                            maxWidth: '1333px',
                          }}>
                          {t('auth:event-other-authprovider')}
                        </div>
                        <div
                          style={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'center',
                            justifyItems: 'center',
                            alignContent: 'center',
                          }}>
                          {event.eventauth.registerPageUrl && (
                            <VyButton
                              style={{ display: 'flex', margin: '1rem' }}
                              type="submit"
                              event={event}
                              className={`my-1 max-w-[30%]`}
                              onClick={() => {
                                if (window && event) {
                                  window.open(event.eventauth.registerPageUrl);
                                }
                              }}>
                              {t('auth:participantsignup')}
                            </VyButton>
                          )}
                          <Link href={'/login'}>
                            <VyButton style={{ display: 'flex', margin: '1rem' }} event={event} type="submit" className={`my-1 max-w-[30%]`}>
                              {t('auth:exhibitorsingup')}
                            </VyButton>
                          </Link>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div>
                    <p style={{ fontSize: '20px' }}>{t('common:event-not-exist')}</p>
                  </div>
                )}
              </div>
            </Container>
          </MobilePreviewWrapper>
        ) : (
          <div className="h-full">
            {!templateEditMode && isEventHost() && <DrawerEvent />}
            {/* this is where we render the pagebuilder */}
            {user && user.role !== 'PARTICIPANT_ANONYM' ? <BlackAccountHeader showMenu /> : null}
            {templateEditMode && <PageSettingsSidebar />}
            <PBUpdateButtons isUpdating={isUpdating} onUpdate={updatePageBuilder} onCancel={cancelChanges} />
            {!isMobilePreview && (
              <div className="fixed w-screen h-screen -z-10">
                <PageBgOverlay />
              </div>
            )}

            <MobilePreviewWrapper>
              {isMobilePreview && <PageBgOverlay />}
              {isPagebuilderActive() ? <PageBuilderContent isLandingPage={false} /> : <WithoutPageBuilder />}
            </MobilePreviewWrapper>
          </div>
        )}
      </EventWrapper>
    </>
  );
}

export const getStaticProps: GetStaticProps = async (context) => {
  return await getEventStaticProps(context);
};

export async function getStaticPaths() {
  return {
    paths: await getEventStaticPaths(),
    fallback: true,
  };
}

export default SingUpForEvent;
